import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  BarChart3,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Users,
  Building,
  FileText,
  HardDrive,
} from "lucide-react";
import { Id } from "@/convex/_generated/dataModel";

export const Route = createFileRoute(
  "/_super/super/control-panel/$companyId/usage",
)({
  component: UsageMetrics,
});

function UsageMetrics() {
  const { companyId } = Route.useParams();

  const details = useQuery(
    api.companies.subscriptions.queries.getCompanySubscriptionDetails,
    {
      companyId: companyId as Id<"companies">,
    },
  );

  if (!details) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-1/2" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-3 bg-muted rounded" />
                <div className="h-3 bg-muted rounded w-5/6" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const { company, subscription, plan, limitsStatus } = details;

  if (!subscription || !limitsStatus) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <BarChart3 className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Sin datos de uso</h3>
          <p className="text-sm text-muted-foreground">
            Esta empresa no tiene una suscripción activa para mostrar métricas
          </p>
        </CardContent>
      </Card>
    );
  }

  const getStatusIcon = (status: {
    isOverLimit: boolean;
    isNearLimit: boolean;
  }) => {
    if (status.isOverLimit) {
      return <AlertTriangle className="h-5 w-5 text-destructive" />;
    }
    if (status.isNearLimit) {
      return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    }
    return <CheckCircle className="h-5 w-5 text-green-500" />;
  };

  const getStatusVariant = (status: {
    isOverLimit: boolean;
    isNearLimit: boolean;
  }): "destructive" | "default" | "secondary" => {
    if (status.isOverLimit) return "destructive";
    if (status.isNearLimit) return "default";
    return "secondary";
  };

  const metrics = [
    {
      id: "users",
      title: "Usuarios",
      icon: Users,
      data: limitsStatus.users,
      unit: "usuarios",
    },
    {
      id: "branches",
      title: "Sucursales",
      icon: Building,
      data: limitsStatus.branches,
      unit: "sucursales",
    },
    {
      id: "invoices",
      title: "Facturas (mes actual)",
      icon: FileText,
      data: limitsStatus.invoices,
      unit: "facturas",
    },
    {
      id: "storage",
      title: "Almacenamiento",
      icon: HardDrive,
      data: limitsStatus.storage,
      unit: "GB",
    },
  ];

  const hasWarnings =
    limitsStatus.users.isNearLimit ||
    limitsStatus.branches.isNearLimit ||
    limitsStatus.invoices.isNearLimit;

  const hasErrors =
    limitsStatus.users.isOverLimit ||
    limitsStatus.branches.isOverLimit ||
    limitsStatus.invoices.isOverLimit;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Resumen de Uso
          </CardTitle>
          <CardDescription>
            Métricas de uso de recursos para {company.name}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                {hasErrors ? (
                  <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-destructive" />
                  </div>
                ) : hasWarnings ? (
                  <div className="h-12 w-12 rounded-full bg-yellow-500/10 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-yellow-500" />
                  </div>
                ) : (
                  <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-green-500" />
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Estado general</p>
                <p className="text-xl font-semibold">
                  {hasErrors
                    ? "Límites excedidos"
                    : hasWarnings
                      ? "Cerca del límite"
                      : "Dentro del límite"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Plan actual</p>
                <p className="text-xl font-semibold">{plan?.name}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {hasErrors && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Límites excedidos</AlertTitle>
          <AlertDescription>
            La empresa ha excedido uno o más límites de su plan. Se requiere
            acción inmediata para evitar interrupciones en el servicio.
          </AlertDescription>
        </Alert>
      )}

      {hasWarnings && !hasErrors && (
        <Alert variant="default" className="border-yellow-500/50">
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
          <AlertTitle>Advertencia: Límites cercanos al máximo</AlertTitle>
          <AlertDescription>
            Algunos recursos están cerca del límite. Considera actualizar el
            plan o aumentar los límites para evitar interrupciones.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {metrics.map((metric) => {
          const MetricIcon = metric.icon;
          return (
            <Card key={metric.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MetricIcon className="h-5 w-5" />
                    {metric.title}
                  </CardTitle>
                  {getStatusIcon(metric.data)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-3xl font-bold">
                      {metric.data.current}
                    </span>
                    <span className="text-muted-foreground">
                      / {metric.data.max} {metric.unit}
                    </span>
                  </div>
                  <Progress value={metric.data.percentage} className="h-3" />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-sm text-muted-foreground">
                      {metric.data.available} {metric.unit} disponibles
                    </p>
                    <Badge variant={getStatusVariant(metric.data)}>
                      {metric.data.percentage}%
                    </Badge>
                  </div>
                </div>

                {metric.data.isOverLimit && (
                  <Alert variant="destructive" className="py-2">
                    <AlertDescription className="text-xs">
                      Límite excedido en {metric.data.current - metric.data.max}{" "}
                      {metric.unit}
                    </AlertDescription>
                  </Alert>
                )}

                {metric.data.isNearLimit && !metric.data.isOverLimit && (
                  <Alert
                    variant="default"
                    className="py-2 border-yellow-500/50"
                  >
                    <AlertDescription className="text-xs text-yellow-700 dark:text-yellow-500">
                      Cerca del límite. Quedan {metric.data.available}{" "}
                      {metric.unit}
                    </AlertDescription>
                  </Alert>
                )}

                {!metric.data.isNearLimit && !metric.data.isOverLimit && (
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-500">
                    <CheckCircle className="h-4 w-4" />
                    <span>Uso normal</span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Detalles del Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <p className="text-sm font-medium">Límites del Plan</p>
              <div className="space-y-1 text-sm text-muted-foreground">
                <div className="flex justify-between">
                  <span>Usuarios máximos:</span>
                  <span className="font-medium">
                    {subscription.limits.maxUsers}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Sucursales máximas:</span>
                  <span className="font-medium">
                    {subscription.limits.maxBranches}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Facturas por mes:</span>
                  <span className="font-medium">
                    {subscription.limits.invoicesPerMonth}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Almacenamiento:</span>
                  <span className="font-medium">
                    {subscription.limits.storageGB} GB
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Uso Actual</p>
              <div className="space-y-1 text-sm text-muted-foreground">
                <div className="flex justify-between">
                  <span>Usuarios activos:</span>
                  <span className="font-medium">
                    {limitsStatus.users.current}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Sucursales activas:</span>
                  <span className="font-medium">
                    {limitsStatus.branches.current}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Facturas este mes:</span>
                  <span className="font-medium">
                    {limitsStatus.invoices.current}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Almacenamiento usado:</span>
                  <span className="font-medium">
                    {limitsStatus.storage.current} GB
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
